import { LineItem } from 'pupdocs/types';
import React, { createContext, ReactNode, useContext, useReducer } from 'react';

// ********************************************************************************
// Reference documentation:
// https://kentcdodds.com/blog/how-to-use-react-context-effectively
// ********************************************************************************

interface UseDetailsState {
  lineItem: LineItem | undefined;
}

type Dispatch = (action: DetailsActions) => void;

export type DetailsActions = { type: 'SET_LINE_ITEM'; lineItem: LineItem };

export const detailsReducer = (
  state: UseDetailsState,
  action: DetailsActions,
): UseDetailsState => {
  switch (action.type) {
    case 'SET_LINE_ITEM': {
      const { lineItem } = action;
      return { ...state, lineItem };
    }
    default: {
      return state;
    }
  }
};

const DetailsStateContext = createContext<
  { state: UseDetailsState; dispatch: Dispatch } | undefined
>(undefined);

interface DetailsProviderProps {
  children: ReactNode;
  reducerInitialState?: UseDetailsState;
}

export function DetailsProvider(props: DetailsProviderProps) {
  const { children, reducerInitialState } = props;
  const initialState = reducerInitialState ?? {
    lineItem: undefined,
  };
  const [state, dispatch] = useReducer(detailsReducer, initialState);

  const value = { state, dispatch };

  return (
    <DetailsStateContext.Provider value={value}>{children}</DetailsStateContext.Provider>
  );
}

export function useDetailsState() {
  const context = useContext(DetailsStateContext);
  if (!context) {
    throw new Error('useDetailsState must be used within a DetailsProvider');
  }

  return context;
}
